/**
 * Formatting and parsing at the edge of the UI (docs/01-architectuur/11-frontend.md, Taal en notatie):
 * the API stays ISO, UTC, seconds and cents; the UI shows DD-MM-JJJJ, a decimal comma and € 1.234,56, in the time
 * zone of the user (default Europe/Amsterdam). Amounts stay integers in cents until display.
 */
import { DEFAULT_TZ } from '@mkcore/shared';

/** The preferences of the signed-in person (CO-0021), set once the profile loads; the formatters read them. */
export interface FormatPreferences {
  locale: Locale;
  timezone: string;
  dateFormat: 'dd-mm-yyyy' | 'yyyy-mm-dd' | 'dd/mm/yyyy' | 'mm/dd/yyyy';
  durationFormat: 'clock' | 'decimal';
  weekStart: number;
}
let prefs: FormatPreferences = { locale: 'nl', timezone: DEFAULT_TZ, dateFormat: 'dd-mm-yyyy', durationFormat: 'clock', weekStart: 1 };
export function setFormatPreferences(p: Partial<FormatPreferences>) {
  prefs = { ...prefs, ...p };
}
export const formatPreferences = () => prefs;

export type Locale = 'nl' | 'en';
const tag = (l: Locale) => (l === 'nl' ? 'nl-NL' : 'en-GB');
export { DEFAULT_TZ };

function parts(d: Date, tz: string) {
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return { y: p.year!, m: p.month!, d: p.day!, h: p.hour!, min: p.minute! };
}

const toDate = (v: string | Date) => (typeof v === 'string' ? new Date(v) : v);

/** 25-09-2026 (both languages use day-month-year). A plain ISO date (2026-09-25) is not shifted by time zone. */
export function formatDate(v: string | Date, tz = prefs.timezone): string {
  const ymd = (y: string, m: string, d: string) => {
    switch (prefs.dateFormat) {
      case 'yyyy-mm-dd':
        return `${y}-${m}-${d}`;
      case 'dd/mm/yyyy':
        return `${d}/${m}/${y}`;
      case 'mm/dd/yyyy':
        return `${m}/${d}/${y}`;
      default:
        return `${d}-${m}-${y}`;
    }
  };
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-') as [string, string, string];
    return ymd(y, m, d);
  }
  const p = parts(toDate(v), tz);
  return ymd(p.y, p.m, p.d);
}

/** 25-09-2026 10:00 */
export function formatDateTime(v: string | Date, tz = prefs.timezone): string {
  const p = parts(toDate(v), tz);
  return `${formatDate(v, tz)} ${p.h}:${p.min}`;
}

export function formatTime(v: string | Date, tz = prefs.timezone): string {
  const p = parts(toDate(v), tz);
  return `${p.h}:${p.min}`;
}

export function formatNumber(n: number, locale: Locale = 'nl', digits = 0): string {
  return new Intl.NumberFormat(tag(locale), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}

/** € 1.234,56 from integer cents; integer arithmetic until the final division. */
export function formatMoney(cents: number, currency = 'EUR', locale: Locale = 'nl'): string {
  if (!Number.isInteger(cents)) throw new Error('Amounts are integer cents');
  const neg = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.trunc(abs / 100);
  const frac = String(abs % 100).padStart(2, '0');
  const grouped = new Intl.NumberFormat(tag(locale), { maximumFractionDigits: 0, useGrouping: true }).format(whole);
  const dec = locale === 'nl' ? ',' : '.';
  const symbol = currency === 'EUR' ? '€' : currency;
  return `${neg ? '-' : ''}${symbol} ${grouped}${dec}${frac}`;
}

/**
 * AI costs from millionths of the currency (cost_micros, CO-0970): as money from a cent up, with four decimals below
 * a cent, so a call of a few tokens does not show as zero.
 */
export function formatMicros(micros: number, currency = 'USD', locale: Locale = 'nl'): string {
  if (micros === 0 || Math.abs(micros) >= 10_000) return formatMoney(Math.round(micros / 10_000), currency, locale);
  const v = new Intl.NumberFormat(tag(locale), { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(micros / 1_000_000);
  return `${currency === 'EUR' ? '€' : currency} ${v}`;
}

/** 1:30 for 5400 seconds (hours:minutes, the notation of the weekly timesheet), or 1,50 when the person prefers decimals. */
export function formatDuration(seconds: number): string {
  if (prefs.durationFormat === 'decimal') return formatHours(seconds, prefs.locale, 2);
  const neg = seconds < 0;
  const total = Math.round(Math.abs(seconds) / 60);
  const h = Math.trunc(total / 60);
  const m = total % 60;
  return `${neg ? '-' : ''}${h}:${String(m).padStart(2, '0')}`;
}

/** 1,5 for 5400 seconds (decimal hours). */
export function formatHours(seconds: number, locale: Locale = 'nl', digits = 2): string {
  return formatNumber(seconds / 3600, locale, digits);
}

export { parseDuration } from '@mkcore/shared';

/** Parses "1.234,56", "1234,5", "€ 12", "-3,10" (nl) or "1,234.56" (en) to integer cents; null when unreadable. */
export function parseMoney(input: string, locale: Locale = 'nl'): number | null {
  let s = input.trim().replace(/[€\s]/g, '').replace(/^eur/i, '');
  if (!s) return null;
  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);
  const [group, dec] = locale === 'nl' ? ['.', ','] : [',', '.'];
  if (!new RegExp(`^\\d{1,3}(\\${group}\\d{3})*(\\${dec}\\d{1,2})?$|^\\d+(\\${dec}\\d{1,2})?$`).test(s)) return null;
  const [whole, frac = ''] = s.split(dec);
  const cents = Number(whole!.replaceAll(group, '')) * 100 + Number(frac.padEnd(2, '0'));
  return neg ? -cents : cents;
}
